import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Alert, Spinner } from "react-bootstrap";
import moment from "moment";
import { toast } from "react-toastify";
import Header from "../../layouts/Header";
import Footer from "../../layouts/Footer";
import AdminEmptyState from "../../components/admin/AdminEmptyState";
import { useWhatsAppNotifications } from "../../context/WhatsAppNotificationContext";
import {
  formatReplyWindowRemaining,
  formatWaId,
  getDisplayName,
  getMessageDisplayBody,
  getMessageMediaUrl,
  getMessageStatusLabel,
  getMessageTypeMeta,
  getOutboundMessageTickState,
  getReplyWindowState,
  getWhatsAppConversationMessages,
  isImageMessage,
  isSpecialMessageType,
  listWhatsAppConversations,
  normalizeConversationList,
  normalizeConversationPagination,
  normalizeMessageList,
  normalizeMessagePagination,
  replyToWhatsAppConversation,
  replyToWhatsAppConversationWithMedia,
} from "../../helper/whatsapp_inbox_helper";
import { WA_PREVIEW_CONVERSATIONS, WA_PREVIEW_MESSAGES } from "./waPreviewData";

const POLL_INTERVAL_MS = 15000;
const MESSAGE_LIMIT = 100;
const NEAR_BOTTOM_THRESHOLD_PX = 80;
const MAX_ATTACHMENT_BYTES = 16 * 1024 * 1024;

const QUICK_EMOJIS = [
  "😀", "😂", "😍", "🥰", "😊", "😎", "🤔", "😢", "🙏", "👍",
  "👎", "👋", "🎉", "❤️", "🔥", "✅", "⭐", "📍", "📞", "💬",
  "📷", "📎", "✈️", "🏨", "🛒", "💳", "⏰", "📅", "🙂", "😅",
];

const AVATAR_COLORS = [
  "#6a7175", "#009688", "#00a884", "#7c4dff", "#ff5722",
  "#0288d1", "#c2185b", "#5c6bc0", "#00796b", "#f4511e",
];

const conversationPreview = (conversation) => {
  const text = getMessageDisplayBody({ body: conversation?.lastMessage });
  const cleaned = String(text || "").trim();
  if (!cleaned) return "No messages yet";
  return cleaned.length > 56 ? `${cleaned.slice(0, 56)}…` : cleaned;
};

const getMessageKey = (message) =>
  message?._id || `${message?.createdAt || ""}-${message?.direction || ""}-${message?.body || ""}`;

const sortMessages = (items = []) =>
  [...items].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

const getAvatarInitials = (name, waId) => {
  const label = String(name || formatWaId(waId) || "?").trim();
  const parts = label.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return label.slice(0, 2).toUpperCase();
};

const getAvatarColor = (seed = "") => {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const formatListTime = (dateValue) => {
  if (!dateValue) return "";
  const date = moment(dateValue);
  const now = moment();
  if (date.isSame(now, "day")) return date.format("h:mm A");
  if (date.isSame(now.clone().subtract(1, "day"), "day")) return "Yesterday";
  if (date.isSame(now, "week")) return date.format("dddd");
  if (date.isSame(now, "year")) return date.format("DD/MM/YYYY");
  return date.format("DD/MM/YYYY");
};

const formatDateDivider = (dateValue) => {
  const date = moment(dateValue);
  const now = moment();
  if (date.isSame(now, "day")) return "Today";
  if (date.isSame(now.clone().subtract(1, "day"), "day")) return "Yesterday";
  return date.format("MMMM D, YYYY");
};

const sameMessageGroup = (a, b) => {
  if (!a || !b) return false;
  if (a.direction !== b.direction) return false;
  if (a.status === "auto_replied" || b.status === "auto_replied") {
    return a.status === b.status;
  }
  return moment(a.createdAt).isSame(b.createdAt, "day");
};

const getGroupPosition = (messages, index) => {
  const current = messages[index];
  const prev = messages[index - 1];
  const next = messages[index + 1];
  const withPrev = sameMessageGroup(current, prev);
  const withNext = sameMessageGroup(current, next);

  if (!withPrev && !withNext) return "single";
  if (!withPrev && withNext) return "first";
  if (withPrev && withNext) return "middle";
  return "last";
};

const buildThreadItems = (items = []) => {
  const groups = [];
  let currentKey = null;

  items.forEach((message, index) => {
    const key = moment(message.createdAt).format("YYYY-MM-DD");
    if (key !== currentKey) {
      groups.push({ type: "divider", key: `divider-${key}`, label: formatDateDivider(message.createdAt) });
      currentKey = key;
    }
    groups.push({
      type: "message",
      key: getMessageKey(message),
      message,
      groupPosition: getGroupPosition(items, index),
    });
  });

  return groups;
};

const MessageStatusIcon = ({ message }) => {
  if (message?.direction === "inbound") return null;

  const tickState = getOutboundMessageTickState(message);
  const label = getMessageStatusLabel(message);

  if (tickState === "failed") {
    return (
      <i
        className="ri-error-warning-line wa-bubble__status wa-bubble__status--failed"
        title={label}
      />
    );
  }

  if (tickState === "auto_replied") {
    return <i className="ri-robot-line wa-bubble__status" title={label} />;
  }

  if (tickState === "sent") {
    return <i className="ri-check-line wa-bubble__status wa-bubble__status--sent" title={label} />;
  }

  if (tickState === "read") {
    return <i className="ri-check-double-line wa-bubble__status wa-bubble__status--read" title={label} />;
  }

  return <i className="ri-check-double-line wa-bubble__status wa-bubble__status--delivered" title={label} />;
};

const MessageBubbleContent = ({ message }) => {
  const mediaUrl = getMessageMediaUrl(message);
  const imageMessage = isImageMessage(message);
  const fileName = message?.fileName || message?.media?.filename || "Attachment";
  const displayBody = getMessageDisplayBody(message);
  const typeMeta = getMessageTypeMeta(message);
  const specialType = isSpecialMessageType(message);
  const unsupported =
    String(message?.messageType || "").toLowerCase() === "unsupported" ||
    /^\[unsupported message received\]$/i.test(String(message?.body || "").trim());

  return (
    <>
      {mediaUrl && imageMessage && (
        <a href={mediaUrl} target="_blank" rel="noreferrer" className="wa-bubble__media-link">
          <img src={mediaUrl} alt={fileName} className="wa-bubble__media" />
        </a>
      )}
      {mediaUrl && !imageMessage && (
        <a href={mediaUrl} target="_blank" rel="noreferrer" className="wa-bubble__file">
          <i className="ri-file-text-line" />
          <span>{fileName}</span>
        </a>
      )}
      {displayBody && (
        <span className={unsupported ? "wa-bubble__notice" : specialType ? "wa-bubble__special" : undefined}>
          {specialType && typeMeta?.icon && !mediaUrl && (
            <i className={`${typeMeta.icon} wa-bubble__type-icon`} aria-hidden="true" />
          )}
          <span>{displayBody}</span>
        </span>
      )}
      {!displayBody && !mediaUrl && "[message]"}
    </>
  );
};

export default function WhatsAppInbox() {
  const [searchParams, setSearchParams] = useSearchParams();
  const uiPreview = searchParams.get("uiPreview") === "1";
  const { markConversationRead, refreshNotifications } = useWhatsAppNotifications();
  const [conversations, setConversations] = useState([]);
  const [conversationsLoading, setConversationsLoading] = useState(true);
  const [conversationPage, setConversationPage] = useState(1);
  const [conversationPagination, setConversationPagination] = useState(null);
  const [listSearch, setListSearch] = useState("");
  const [listFilter, setListFilter] = useState("all");

  const [selectedWaId, setSelectedWaId] = useState(null);
  const [selectedContactName, setSelectedContactName] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [olderPage, setOlderPage] = useState(null);
  const [loadingOlder, setLoadingOlder] = useState(false);

  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [pendingAttachment, setPendingAttachment] = useState(null);

  const threadRef = useRef(null);
  const composeRef = useRef(null);
  const fileInputRef = useRef(null);
  const emojiWrapRef = useRef(null);
  const selectedWaIdRef = useRef(null);
  const conversationPageRef = useRef(conversationPage);
  const pollingRef = useRef(false);
  const previewBootstrappedRef = useRef(false);
  const conversationsInFlightRef = useRef(false);
  const threadInFlightRef = useRef(false);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    document.body.classList.add("wa-inbox-mode");
    return () => document.body.classList.remove("wa-inbox-mode");
  }, []);

  useEffect(() => {
    if (!showEmojiPicker) return undefined;

    const handleOutsideClick = (event) => {
      if (emojiWrapRef.current && !emojiWrapRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener("click", handleOutsideClick);
    return () => document.removeEventListener("click", handleOutsideClick);
  }, [showEmojiPicker]);

  useEffect(() => {
    selectedWaIdRef.current = selectedWaId;
  }, [selectedWaId]);

  useEffect(() => {
    conversationPageRef.current = conversationPage;
  }, [conversationPage]);

  const replyWindow = useMemo(() => getReplyWindowState(messages), [messages]);

  const filteredConversations = useMemo(() => {
    let rows = conversations;

    if (listFilter === "unread") {
      rows = rows.filter((conversation) => Number(conversation.unreadCount) > 0);
    }

    const query = listSearch.trim().toLowerCase();
    if (!query) return rows;

    return rows.filter((conversation) => {
      const name = getDisplayName(conversation.contactName, conversation.waId).toLowerCase();
      const phone = formatWaId(conversation.waId).toLowerCase();
      const preview = conversationPreview(conversation).toLowerCase();
      return name.includes(query) || phone.includes(query) || preview.includes(query);
    });
  }, [conversations, listFilter, listSearch]);

  const threadItems = useMemo(() => buildThreadItems(messages), [messages]);

  const scrollThreadToBottom = useCallback(() => {
    const node = threadRef.current;
    if (node) {
      node.scrollTop = node.scrollHeight;
      setShowScrollDown(false);
    }
  }, []);

  useEffect(() => {
    if (messagesLoading || !selectedWaId || !messages.length) return;
    requestAnimationFrame(() => {
      scrollThreadToBottom();
    });
  }, [messagesLoading, selectedWaId, scrollThreadToBottom]); // intentionally not depending on messages.length churn during poll

  const isNearThreadBottom = useCallback(() => {
    const node = threadRef.current;
    if (!node) return true;
    return node.scrollHeight - node.scrollTop - node.clientHeight <= NEAR_BOTTOM_THRESHOLD_PX;
  }, []);

  const handleThreadScroll = useCallback(() => {
    setShowScrollDown(!isNearThreadBottom());
  }, [isNearThreadBottom]);

  const fetchConversations = useCallback(async (page = conversationPageRef.current, silent = false) => {
    if (uiPreview) {
      setConversations(WA_PREVIEW_CONVERSATIONS);
      setConversationPagination({ page: 1, totalPages: 1, total: WA_PREVIEW_CONVERSATIONS.length });
      setConversationsLoading(false);
      setLoadError(null);
      if (silent) setLastRefreshedAt(new Date());
      return;
    }

    if (conversationsInFlightRef.current) {
      return;
    }

    conversationsInFlightRef.current = true;
    if (!silent) {
      setConversationsLoading(true);
      setLoadError(null);
    }

    try {
      const res = await listWhatsAppConversations({ page, limit: 20 });
      if (res?.success === false) {
        const message = res?.message || "Failed to load conversations";
        setLoadError(message);
        if (!silent) {
          toast.error(message);
          setConversations([]);
        }
        return;
      }

      const rows = normalizeConversationList(res);
      setConversations(rows);
      setConversationPagination(normalizeConversationPagination(res));
      setLoadError(null);
      if (silent) setLastRefreshedAt(new Date());
    } catch (error) {
      const message = error?.message || "Failed to load conversations";
      setLoadError(message);
      if (!silent) {
        toast.error(message);
        setConversations([]);
      }
    } finally {
      conversationsInFlightRef.current = false;
      if (!silent) setConversationsLoading(false);
    }
  }, [uiPreview]);

  const loadThreadPage = useCallback(async (waId, page) => {
    if (uiPreview) {
      return {
        messages: WA_PREVIEW_MESSAGES[waId] || [],
        pagination: { page: 1, totalPages: 1 },
      };
    }

    const res = await getWhatsAppConversationMessages(waId, { page, limit: MESSAGE_LIMIT });
    if (res?.success === false) {
      throw new Error(res?.message || "Failed to load messages");
    }

    return {
      messages: normalizeMessageList(res),
      pagination: normalizeMessagePagination(res),
    };
  }, [uiPreview]);

  const fetchLatestThreadMessages = useCallback(async (waId) => {
    const first = await loadThreadPage(waId, 1);
    const totalPages = first.pagination?.totalPages || 1;

    if (totalPages <= 1 || uiPreview) {
      return {
        messages: first.messages,
        pagination: first.pagination,
        olderPage: null,
      };
    }

    const latest = await loadThreadPage(waId, totalPages);
    return {
      messages: latest.messages,
      pagination: latest.pagination,
      olderPage: totalPages > 1 ? totalPages - 1 : null,
    };
  }, [loadThreadPage, uiPreview]);

  const refreshThreadMessages = useCallback(async (silent = true) => {
    const waId = selectedWaIdRef.current;
    if (!waId || pollingRef.current || threadInFlightRef.current) return false;

    pollingRef.current = true;
    threadInFlightRef.current = true;
    if (silent) setIsPolling(true);

    const shouldAutoScroll = isNearThreadBottom();

    try {
      const latest = await fetchLatestThreadMessages(waId);
      let hasNewMessages = false;

      setMessages((prev) => {
        const existingKeys = new Set(prev.map(getMessageKey));
        const incoming = latest.messages.filter((message) => !existingKeys.has(getMessageKey(message)));
        hasNewMessages = incoming.length > 0;

        if (!incoming.length) {
          return prev;
        }

        return sortMessages([...prev, ...incoming]);
      });

      setOlderPage((prevOlderPage) => prevOlderPage ?? latest.olderPage);

      if (hasNewMessages && shouldAutoScroll) {
        setTimeout(scrollThreadToBottom, 50);
      }

      if (silent) setLastRefreshedAt(new Date());
      return hasNewMessages;
    } catch (error) {
      if (!silent) {
        toast.error(error?.message || "Failed to refresh messages");
      }
      return false;
    } finally {
      pollingRef.current = false;
      threadInFlightRef.current = false;
      if (silent) setIsPolling(false);
    }
  }, [fetchLatestThreadMessages, isNearThreadBottom, scrollThreadToBottom]);

  const openConversation = useCallback(async (conversation, silent = false) => {
    const waId = conversation?.waId;
    if (!waId) return;

    setSelectedWaId(waId);
    setSelectedContactName(conversation?.contactName || null);
    if (!silent) {
      setMessages([]);
      setOlderPage(null);
    }
    setMessagesLoading(true);

    try {
      const latest = await fetchLatestThreadMessages(waId);
      setMessages(sortMessages(latest.messages));
      setOlderPage(latest.olderPage);
      setLastRefreshedAt(new Date());
      if (!uiPreview) {
        // Notifications/read endpoints may be missing on older API deploys —
        // never let that wipe a successfully loaded thread.
        try {
          await markConversationRead(waId);
        } catch {
          // ignore mark-read failures
        }
        try {
          await refreshNotifications();
        } catch {
          // ignore notification refresh failures
        }
      }
    } catch (error) {
      if (!silent) toast.error(error?.message || "Failed to load messages");
      setMessages([]);
      setOlderPage(null);
    } finally {
      setMessagesLoading(false);
      if (!silent) {
        requestAnimationFrame(() => {
          setTimeout(scrollThreadToBottom, 0);
        });
      }
    }
  }, [fetchLatestThreadMessages, markConversationRead, refreshNotifications, scrollThreadToBottom, uiPreview]);

  const refreshInbox = useCallback(async (silent = true) => {
    await fetchConversations(conversationPageRef.current, silent);
    if (selectedWaIdRef.current) {
      await refreshThreadMessages(silent);
    } else if (silent) {
      setLastRefreshedAt(new Date());
    }
  }, [fetchConversations, refreshThreadMessages]);

  useEffect(() => {
    fetchConversations(conversationPage);
  }, [conversationPage, fetchConversations]);

  useEffect(() => {
    if (uiPreview && !previewBootstrappedRef.current && WA_PREVIEW_CONVERSATIONS.length) {
      previewBootstrappedRef.current = true;
      openConversation(WA_PREVIEW_CONVERSATIONS[0]);
    }
  }, [uiPreview, openConversation]);

  useEffect(() => {
    const waIdFromQuery = searchParams.get("waId");
    if (!waIdFromQuery || conversationsLoading || !conversations.length) {
      return;
    }

    const target = conversations.find((row) => row.waId === waIdFromQuery);
    if (target && target.waId !== selectedWaIdRef.current) {
      openConversation(target);
    }

    if (searchParams.has("waId")) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete("waId");
      setSearchParams(nextParams, { replace: true });
    }
  }, [
    conversations,
    conversationsLoading,
    openConversation,
    searchParams,
    setSearchParams,
  ]);

  useEffect(() => {
    if (uiPreview) return undefined;

    const timer = setInterval(() => {
      if (document.hidden || conversationsInFlightRef.current || threadInFlightRef.current) {
        return;
      }
      refreshInbox(true);
    }, POLL_INTERVAL_MS);

    const handleVisibility = () => {
      if (!document.hidden && !conversationsInFlightRef.current && !threadInFlightRef.current) {
        refreshInbox(true);
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [refreshInbox, uiPreview]);

  useEffect(() => {
    if (!messagesLoading && messages.length) {
      scrollThreadToBottom();
    }
  }, [messagesLoading, messages.length, scrollThreadToBottom]);

  const clearAttachment = useCallback(() => {
    setPendingAttachment((prev) => {
      if (prev?.previewUrl) {
        URL.revokeObjectURL(prev.previewUrl);
      }
      return null;
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const insertEmoji = useCallback((emoji) => {
    const node = composeRef.current;
    const start = node?.selectionStart ?? replyText.length;
    const end = node?.selectionEnd ?? replyText.length;
    const nextValue = `${replyText.slice(0, start)}${emoji}${replyText.slice(end)}`;
    setReplyText(nextValue);
    setShowEmojiPicker(false);

    requestAnimationFrame(() => {
      if (!node) return;
      node.focus();
      const cursor = start + emoji.length;
      node.setSelectionRange(cursor, cursor);
      node.style.height = "auto";
      node.style.height = `${Math.min(node.scrollHeight, 100)}px`;
    });
  }, [replyText]);

  const handleAttachmentPick = () => {
    if (!replyWindow.canReply || sending) return;
    fileInputRef.current?.click();
  };

  const handleAttachmentChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_ATTACHMENT_BYTES) {
      toast.error("File is too large. Maximum size is 16 MB.");
      event.target.value = "";
      return;
    }

    setPendingAttachment((prev) => {
      if (prev?.previewUrl) {
        URL.revokeObjectURL(prev.previewUrl);
      }
      const previewUrl = file.type.startsWith("image/") ? URL.createObjectURL(file) : null;
      return { file, previewUrl, name: file.name, type: file.type };
    });
  };

  const buildPreviewOutboundMessage = (text, attachment, status = "sent") => {
    const isImage = attachment?.file?.type?.startsWith("image/");
    return {
      _id: `preview-${Date.now()}`,
      direction: "outbound",
      body: text || attachment?.name || "",
      messageType: isImage ? "image" : "document",
      mediaUrl: attachment?.previewUrl || "",
      fileName: attachment?.name || "",
      status,
      createdAt: new Date().toISOString(),
    };
  };

  const handleLoadOlder = async () => {
    if (!selectedWaId || !olderPage || loadingOlder) return;

    setLoadingOlder(true);
    const previousScrollHeight = threadRef.current?.scrollHeight || 0;

    try {
      const res = await loadThreadPage(selectedWaId, olderPage);
      setMessages((prev) => [...res.messages, ...prev]);
      setOlderPage(olderPage > 1 ? olderPage - 1 : null);

      requestAnimationFrame(() => {
        const node = threadRef.current;
        if (node) {
          node.scrollTop = node.scrollHeight - previousScrollHeight;
        }
      });
    } catch (error) {
      toast.error(error?.message || "Failed to load older messages");
    } finally {
      setLoadingOlder(false);
    }
  };

  const handleSendReply = async (event) => {
    event.preventDefault();
    const text = replyText.trim();
    const attachment = pendingAttachment;
    if (!selectedWaId || sending || (!text && !attachment)) return;

    if (!replyWindow.canReply) {
      toast.error("Reply window expired. Customer must message again within 24 hours.");
      return;
    }

    if (uiPreview) {
      setReplyText("");
      if (composeRef.current) composeRef.current.style.height = "auto";
      setMessages((prev) => [...prev, buildPreviewOutboundMessage(text, attachment)]);
      clearAttachment();
      setTimeout(scrollThreadToBottom, 50);
      return;
    }

    setSending(true);
    try {
      let res;

      if (attachment?.file) {
        try {
          res = await replyToWhatsAppConversationWithMedia(selectedWaId, {
            message: text,
            file: attachment.file,
          });
        } catch (mediaError) {
          if (text) {
            toast.warn(mediaError?.message || "Could not upload file. Sending text only.");
            res = await replyToWhatsAppConversation(selectedWaId, text);
          } else {
            throw mediaError;
          }
        }
      } else {
        res = await replyToWhatsAppConversation(selectedWaId, text);
      }

      if (res?.success === false) {
        const failedMessage = res?.data;
        toast.error(res?.message || "Failed to send WhatsApp reply");
        if (failedMessage) {
          setMessages((prev) => [...prev, failedMessage]);
          setTimeout(scrollThreadToBottom, 50);
        }
        return;
      }

      toast.success(res?.message || "WhatsApp reply sent");
      setReplyText("");
      if (composeRef.current) {
        composeRef.current.style.height = "auto";
      }
      clearAttachment();
      if (res?.data) {
        setMessages((prev) => [...prev, res.data]);
        setTimeout(scrollThreadToBottom, 50);
      }
      refreshInbox(true);
    } catch (error) {
      toast.error(error?.message || "Failed to send WhatsApp reply");
    } finally {
      setSending(false);
    }
  };

  const handleComposeKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSendReply(event);
    }
  };

  const handleComposeInput = (event) => {
    setReplyText(event.target.value);
    const node = event.target;
    node.style.height = "auto";
    node.style.height = `${Math.min(node.scrollHeight, 100)}px`;
  };

  const closeConversation = () => {
    setSelectedWaId(null);
    setSelectedContactName(null);
    setMessages([]);
    setOlderPage(null);
    setReplyText("");
    setShowScrollDown(false);
    setShowEmojiPicker(false);
    clearAttachment();
  };

  const selectedConversation = conversations.find((item) => item.waId === selectedWaId);
  const selectedDisplayName = getDisplayName(
    selectedContactName || selectedConversation?.contactName,
    selectedWaId
  );
  const unreadTotal = conversations.reduce((sum, row) => sum + (Number(row.unreadCount) || 0), 0);

  const getBubbleClass = (message, groupPosition, isInbound, isFailed) => {
    const parts = ["wa-bubble"];
    if (isInbound) {
      parts.push("wa-bubble--inbound");
    } else if (message.status === "auto_replied") {
      parts.push("wa-bubble--auto");
    } else if (isFailed) {
      parts.push("wa-bubble--failed");
    } else {
      parts.push("wa-bubble--outbound");
    }
    parts.push(`wa-bubble--${groupPosition}`);
    return parts.join(" ");
  };

  return (
    <>
      <Header />
      <div className="main main-app wa-inbox-page">
        {uiPreview && (
          <div className="wa-inbox-preview-banner">
            <i className="ri-eye-line" />
            UI Preview mode — mock data only
          </div>
        )}

        <div className="wa-inbox-page__head">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="/admin/dashboard">Dashboard</Link></li>
              <li className="breadcrumb-item active">WhatsApp Inbox</li>
            </ol>
            <h4 className="main-title mb-0 admin-page-title">WhatsApp Inbox</h4>
          </div>
          <div className="wa-inbox-page__meta">
            <span className="wa-inbox-page__sync">
              {isPolling ? (
                <>
                  <Spinner animation="border" size="sm" />
                  Syncing…
                </>
              ) : (
                <>
                  <span className="wa-inbox-page__sync-dot" />
                  Live · 15s
                </>
              )}
            </span>
            {lastRefreshedAt && (
              <span className="wa-inbox-page__updated">
                {moment(lastRefreshedAt).fromNow()}
              </span>
            )}
          </div>
        </div>

        <div className="wa-inbox">
          <aside className={`wa-inbox__sidebar ${selectedWaId ? "wa-inbox__sidebar--mobile-hidden" : ""}`}>
            <div className="wa-inbox__sidebar-top">
              <span
                className="wa-avatar wa-avatar--toolbar"
                style={{ backgroundColor: "#dfe5e7" }}
                aria-hidden
              >
                <i className="ri-user-3-line" />
              </span>
              <div className="wa-inbox__sidebar-actions">
                <button
                  type="button"
                  className="wa-inbox-icon-btn"
                  onClick={() => refreshInbox(false)}
                  disabled={conversationsLoading || isPolling}
                  title="Refresh"
                >
                  <i className={`ri-refresh-line ${conversationsLoading || isPolling ? "spin" : ""}`} />
                </button>
              </div>
            </div>

            <div className="wa-inbox__search-wrap">
              <i className="ri-search-line wa-inbox__search-icon" />
              <input
                type="search"
                className="wa-inbox__search"
                placeholder="Search or start new chat"
                value={listSearch}
                onChange={(event) => setListSearch(event.target.value)}
              />
            </div>

            <div className="wa-inbox__filters">
              <button
                type="button"
                className={`wa-inbox__filter ${listFilter === "all" ? "is-active" : ""}`}
                onClick={() => setListFilter("all")}
              >
                All
              </button>
              <button
                type="button"
                className={`wa-inbox__filter ${listFilter === "unread" ? "is-active" : ""}`}
                onClick={() => setListFilter("unread")}
              >
                Unread
                {unreadTotal > 0 && <span className="wa-inbox__filter-count">{unreadTotal}</span>}
              </button>
            </div>

            <div className="wa-inbox__list">
              {conversationsLoading ? (
                <div className="wa-inbox__centered">
                  <Spinner animation="border" size="sm" />
                  <p className="wa-inbox__loading-text">Loading chats…</p>
                </div>
              ) : loadError ? (
                <div className="wa-inbox__error-state">
                  <i className="ri-wifi-off-line" />
                  <strong>Could not load chats</strong>
                  <p>{loadError}</p>
                  <button
                    type="button"
                    className="wa-inbox__retry-btn"
                    onClick={() => fetchConversations(conversationPage, false)}
                  >
                    <i className="ri-refresh-line" />
                    Try again
                  </button>
                </div>
              ) : filteredConversations.length === 0 ? (
                <AdminEmptyState
                  icon="ri-whatsapp-line"
                  title={listSearch || listFilter === "unread" ? "No chats found" : "No WhatsApp conversations yet"}
                  description={
                    listSearch || listFilter === "unread"
                      ? "Try a different search or filter."
                      : "Messages from the website WhatsApp button will appear here."
                  }
                />
              ) : (
                filteredConversations.map((conversation) => {
                  const isActive = conversation.waId === selectedWaId;
                  const unreadCount = Number(conversation.unreadCount) || 0;
                  const displayName = getDisplayName(conversation.contactName, conversation.waId);
                  const avatarSeed = conversation.waId || displayName;
                  const isRecentUnread = unreadCount > 0 && moment(conversation.lastMessageAt).isSame(moment(), "day");

                  return (
                    <button
                      key={conversation.waId}
                      type="button"
                      className={`wa-chat-item ${isActive ? "is-active" : ""} ${unreadCount > 0 ? "has-unread" : ""}`}
                      onClick={() => openConversation(conversation)}
                    >
                      <span
                        className="wa-avatar"
                        style={{ backgroundColor: getAvatarColor(avatarSeed) }}
                        aria-hidden
                      >
                        {getAvatarInitials(conversation.contactName, conversation.waId)}
                      </span>
                      <span className="wa-chat-item__body">
                        <span className="wa-chat-item__row">
                          <span className="wa-chat-item__name">{displayName}</span>
                          <span className={`wa-chat-item__time ${isRecentUnread ? "is-unread" : ""}`}>
                            {formatListTime(conversation.lastMessageAt)}
                          </span>
                        </span>
                        <span className="wa-chat-item__row">
                          <span className="wa-chat-item__preview">{conversationPreview(conversation)}</span>
                          {unreadCount > 0 && (
                            <span className="wa-chat-item__badge">
                              {unreadCount > 99 ? "99+" : unreadCount}
                            </span>
                          )}
                        </span>
                      </span>
                    </button>
                  );
                })
              )}
            </div>

            {conversationPagination && conversationPagination.totalPages > 1 && (
              <div className="wa-inbox__pager">
                <button
                  type="button"
                  className="wa-inbox__pager-btn"
                  disabled={conversationPage <= 1 || conversationsLoading}
                  onClick={() => setConversationPage((page) => Math.max(1, page - 1))}
                >
                  <i className="ri-arrow-left-s-line" />
                </button>
                <span>
                  {conversationPagination.page} / {conversationPagination.totalPages}
                </span>
                <button
                  type="button"
                  className="wa-inbox__pager-btn"
                  disabled={conversationPage >= conversationPagination.totalPages || conversationsLoading}
                  onClick={() => setConversationPage((page) => page + 1)}
                >
                  <i className="ri-arrow-right-s-line" />
                </button>
              </div>
            )}
          </aside>

          <section className={`wa-inbox__chat ${selectedWaId ? "is-open" : ""}`}>
            {!selectedWaId ? (
              <div className="wa-inbox__welcome">
                <div className="wa-inbox__welcome-inner">
                  <div className="wa-inbox__welcome-icon">
                    <i className="ri-whatsapp-line" />
                  </div>
                  <h4>WhatsApp Inbox</h4>
                  <p>Send and receive messages from your customers.</p>
                  <p className="wa-inbox__welcome-sub">
                    Select a chat to start messaging. Replies work within 24 hours of the customer&apos;s last message.
                  </p>
                </div>
                <footer className="wa-inbox__welcome-foot">
                  <i className="ri-lock-2-line" />
                  Your personal messages are end-to-end encrypted on WhatsApp.
                </footer>
              </div>
            ) : (
              <>
                <header className="wa-chat-header">
                  <button
                    type="button"
                    className="wa-inbox-icon-btn wa-chat-header__back wa-show-mobile"
                    onClick={closeConversation}
                    aria-label="Back to chats"
                  >
                    <i className="ri-arrow-left-line" />
                  </button>
                  <button type="button" className="wa-chat-header__profile" onClick={() => {}}>
                    <span
                      className="wa-avatar wa-avatar--sm"
                      style={{ backgroundColor: getAvatarColor(selectedWaId || selectedDisplayName) }}
                    >
                      {getAvatarInitials(selectedContactName || selectedConversation?.contactName, selectedWaId)}
                    </span>
                    <span className="wa-chat-header__info">
                      <strong>{selectedDisplayName}</strong>
                      <span>{formatWaId(selectedWaId)}</span>
                    </span>
                  </button>
                  <div className="wa-chat-header__actions">
                    <button type="button" className="wa-inbox-icon-btn" title="Search in chat">
                      <i className="ri-search-line" />
                    </button>
                    <span
                      className={`wa-chat-header__pill wa-hide-mobile ${replyWindow.canReply ? "wa-chat-header__pill--ok" : "wa-chat-header__pill--warn"}`}
                      title="24-hour reply window"
                    >
                      {replyWindow.canReply
                        ? formatReplyWindowRemaining(replyWindow.remainingMs)
                        : replyWindow.reason === "no_inbound"
                          ? "No inbound"
                          : "Expired"}
                    </span>
                  </div>
                </header>

                <div className="wa-inbox__messages-wrap">
                  <div
                    ref={threadRef}
                    className="wa-inbox__messages"
                    onScroll={handleThreadScroll}
                  >
                    {messagesLoading ? (
                      <div className="wa-inbox__centered">
                        <Spinner animation="border" size="sm" />
                      </div>
                    ) : (
                      <>
                        {olderPage && (
                          <div className="wa-inbox__load-more">
                            <button
                              type="button"
                              className="wa-inbox__load-more-btn"
                              onClick={handleLoadOlder}
                              disabled={loadingOlder}
                            >
                              {loadingOlder ? "Loading…" : "Click here for older messages"}
                            </button>
                          </div>
                        )}

                        {messages.length === 0 ? (
                          <AdminEmptyState
                            icon="ri-chat-3-line"
                            title="No messages in this thread"
                            description="This conversation has no stored messages yet."
                          />
                        ) : (
                          threadItems.map((item) => {
                            if (item.type === "divider") {
                              return (
                                <div key={item.key} className="wa-date-divider">
                                  <span>{item.label}</span>
                                </div>
                              );
                            }

                            const message = item.message;
                            const isInbound = message.direction === "inbound";
                            const isFailed = message.status === "failed";
                            const groupPosition = item.groupPosition;

                            return (
                              <div
                                key={item.key}
                                className={`wa-message ${isInbound ? "is-inbound" : "is-outbound"} wa-message--${groupPosition}`}
                              >
                                <div className={getBubbleClass(message, groupPosition, isInbound, isFailed)}>
                                  <span className="wa-bubble__text">
                                    <MessageBubbleContent message={message} />
                                    <span className="wa-bubble__meta">
                                      <span className="wa-bubble__time">
                                        {moment(message.createdAt).format("h:mm A")}
                                      </span>
                                      <MessageStatusIcon message={message} />
                                    </span>
                                  </span>
                                  {isFailed && message.errorMessage && (
                                    <div className="wa-bubble__error">{message.errorMessage}</div>
                                  )}
                                </div>
                              </div>
                            );
                          })
                        )}
                      </>
                    )}
                  </div>

                  {showScrollDown && (
                    <button
                      type="button"
                      className="wa-inbox__scroll-down"
                      onClick={scrollThreadToBottom}
                      aria-label="Scroll to latest messages"
                    >
                      <i className="ri-arrow-down-s-line" />
                    </button>
                  )}
                </div>

                <footer className="wa-compose">
                  {!replyWindow.canReply && (
                    <Alert variant="warning" className="wa-compose__alert py-2 small mb-0">
                      Free-form replies only work within 24 hours of the customer&apos;s last message.
                    </Alert>
                  )}
                  {pendingAttachment && (
                    <div className="wa-compose__attachment">
                      {pendingAttachment.previewUrl ? (
                        <img src={pendingAttachment.previewUrl} alt={pendingAttachment.name} className="wa-compose__attachment-thumb" />
                      ) : (
                        <span className="wa-compose__attachment-file">
                          <i className="ri-file-text-line" />
                          {pendingAttachment.name}
                        </span>
                      )}
                      <button
                        type="button"
                        className="wa-compose__attachment-remove"
                        onClick={clearAttachment}
                        aria-label="Remove attachment"
                      >
                        <i className="ri-close-line" />
                      </button>
                    </div>
                  )}
                  <form className="wa-compose__bar" onSubmit={handleSendReply}>
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="wa-compose__file-input"
                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                      onChange={handleAttachmentChange}
                      tabIndex={-1}
                    />
                    <div className="wa-compose__emoji-wrap" ref={emojiWrapRef}>
                      <button
                        type="button"
                        className="wa-compose__icon-btn"
                        onClick={(event) => {
                          event.stopPropagation();
                          setShowEmojiPicker((open) => !open);
                        }}
                        disabled={!replyWindow.canReply || sending}
                        aria-label="Insert emoji"
                        aria-expanded={showEmojiPicker}
                      >
                        <i className="ri-emotion-line" />
                      </button>
                      {showEmojiPicker && (
                        <div className="wa-emoji-picker" role="listbox" aria-label="Emoji picker">
                          {QUICK_EMOJIS.map((emoji) => (
                            <button
                              key={emoji}
                              type="button"
                              className="wa-emoji-picker__btn"
                              onClick={() => insertEmoji(emoji)}
                              aria-label={`Insert ${emoji}`}
                            >
                              {emoji}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      className="wa-compose__icon-btn"
                      onClick={handleAttachmentPick}
                      disabled={!replyWindow.canReply || sending}
                      aria-label="Attach file"
                    >
                      <i className="ri-attachment-2" />
                    </button>
                    <div className="wa-compose__input-wrap">
                      <textarea
                        ref={composeRef}
                        className="wa-compose__input"
                        rows={1}
                        placeholder={replyWindow.canReply ? "Type a message" : "Reply window expired"}
                        value={replyText}
                        onChange={handleComposeInput}
                        onKeyDown={handleComposeKeyDown}
                        disabled={!replyWindow.canReply || sending}
                      />
                    </div>
                    <button
                      type="submit"
                      className="wa-compose__send"
                      disabled={!replyWindow.canReply || sending || (!replyText.trim() && !pendingAttachment)}
                      aria-label="Send message"
                    >
                      {sending ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        <i className="ri-send-plane-fill" />
                      )}
                    </button>
                  </form>
                </footer>
              </>
            )}
          </section>
        </div>
      </div>
      <Footer />
    </>
  );
}
